/**
 * Notification title and sentence in the user's language.
 *
 * The backend sends an English message plus codes in the status field
 * ("m=<record model>;r=<reference>;v=<status code>,..."), and the record type
 * in `type` ("prosecution.CaseCreation"). From these the text is written in
 * English, Amharic or Afaan Oromo; without codes (older notifications) the
 * English message is shown.
 */
export type Lang = "en" | "am" | "or";

type Words = Record<Lang, string>;

const RECORDS: Record<string, Words> = {
  ReceivingInvestigationFile: { en: "investigation file", am: "የምርመራ መዝገብ", or: "faayilii qorannoo" },
  CaseCreation: { en: "charge", am: "ክስ", or: "himannaa" },
  CourtApplication: { en: "court application", am: "የፍርድ ቤት ማመልከቻ", or: "iyyata mana murtii" },
  AppealApplication: { en: "appeal application", am: "የይግባኝ ማመልከቻ", or: "iyyata ol'iyyannoo" },
  AppealRequest: { en: "appeal", am: "ይግባኝ", or: "ol'iyyannoo" },
  DefendantAppealRequest: { en: "defendant appeal", am: "የተከሳሽ ይግባኝ", or: "ol'iyyannoo himatamaa" },
  HigherAdminFeedbackRequest: { en: "higher authority feedback request", am: "የበላይ አካል አስተያየት ጥያቄ", or: "gaaffii yaada qaama olaanaa" },
  SuspectToWitnessConversion: { en: "suspect-to-witness conversion", am: "ተጠርጣሪን ወደ ምስክር የመቀየር ጥያቄ", or: "shakkamaa gara ragaatti jijjiiruu" },
  Remand: { en: "remand", am: "የጊዜ ቀጠሮ", or: "beellama yeroo" },
  Execution: { en: "execution", am: "የፍርድ አፈጻጸም", or: "raawwii murtii" },
  BailRight: { en: "bail right request", am: "የዋስትና መብት ጥያቄ", or: "gaaffii mirga wabii" },
  WitnessProtectionRequest: { en: "witness protection request", am: "የምስክር ጥበቃ ጥያቄ", or: "gaaffii eegumsa ragaa" },
  AssetReferral: { en: "asset recovery referral", am: "የንብረት ማስመለስ ጉዳይ", or: "dhimma deebisuu qabeenyaa" },
  AssetAppealRequest: { en: "asset recovery appeal", am: "የንብረት ማስመለስ ይግባኝ", or: "ol'iyyannoo deebisuu qabeenyaa" },
  AssetAppealRespondant: { en: "asset recovery appeal", am: "የንብረት ማስመለስ ይግባኝ", or: "ol'iyyannoo deebisuu qabeenyaa" },
  Investigation: { en: "investigation", am: "ምርመራ", or: "qorannoo" },
  CrimeScreeningTeamMember: { en: "crime screening team", am: "የወንጀል ማጣሪያ ቡድን", or: "garee calallii yakkaa" },
  CrimeReference: { en: "incident", am: "ክስተት", or: "taatee" },
  ProsecutorAssignmentHistory: { en: "case", am: "ጉዳይ", or: "dhimma" },
};

const ACTIONS: Record<string, Words> = {
  assigned: { en: "New assignment", am: "አዲስ ምደባ", or: "Ramaddii haaraa" },
  reassigned: { en: "Reassigned to you", am: "እንደገና ተመድቦልዎታል", or: "Irra deebiin isiniif ramadame" },
  status_changed: { en: "Status update", am: "የሁኔታ ለውጥ", or: "Jijjiirama haalaa" },
  sent_to_you: { en: "Sent to you", am: "ወደ እርስዎ ተልኳል", or: "Gara keessanitti ergame" },
};

const SENTENCES: Record<Lang, Record<string, (record: string, ref: string, status: string) => string>> = {
  en: {
    assigned: (r, ref) => `${/^[aeiou]/i.test(r) ? "An" : "A"} ${r}${ref} has been assigned to you.`,
    reassigned: (r, ref) => `${/^[aeiou]/i.test(r) ? "An" : "A"} ${r}${ref} has been assigned to you.`,
    status_changed: (r, ref, s) => `${cap(r)}${ref} is now: ${s}.`,
    sent_to_you: (r, ref, s) => `${cap(r)}${ref} has been sent to you: ${s}.`,
  },
  am: {
    assigned: (r, ref) => `${r}${ref} ተመድቦልዎታል።`,
    reassigned: (r, ref) => `${r}${ref} ተመድቦልዎታል።`,
    status_changed: (r, ref, s) => `${r}${ref} አሁን፦ ${s}።`,
    sent_to_you: (r, ref, s) => `${r}${ref} ወደ እርስዎ ተልኳል፦ ${s}።`,
  },
  or: {
    assigned: (r, ref) => `${cap(r)}${ref} isiniif ramadameera.`,
    reassigned: (r, ref) => `${cap(r)}${ref} isiniif ramadameera.`,
    status_changed: (r, ref, s) => `${cap(r)}${ref} amma: ${s}.`,
    sent_to_you: (r, ref, s) => `${cap(r)}${ref} gara keessanitti ergameera: ${s}.`,
  },
};

// The statuses these notifications carry, by normalized code.
const STATUS_WORDS: [string[], Words][] = [
  [["forward_to_coordinator", "forwarded_to_coordinator", "forwarded_to_cordinator"],
    { en: "Forwarded to coordinator", am: "ወደ አስተባባሪ ተልኳል", or: "Gara qindeessaatti dabarfame" }],
  [["forwarded_to_higher_coordinator"],
    { en: "Forwarded to higher coordinator", am: "ወደ በላይ አስተባባሪ ተልኳል", or: "Qindeessaa olaanaatti dabarfame" }],
  [["forward_to_registrar", "forwarded_to_registrar"],
    { en: "Forwarded to registrar", am: "ወደ ሬጅስትራር ተልኳል", or: "Gara galmeessaatti dabarfame" }],
  [["forwarded_to_higher_registrar"],
    { en: "Forwarded to higher registrar", am: "ወደ በላይ ሬጅስትራር ተልኳል", or: "Galmeessaa olaanaatti dabarfame" }],
  [["returned_to_coordinator", "respond_back_cordinator"],
    { en: "Returned to coordinator", am: "ወደ አስተባባሪ ተመልሷል", or: "Gara qindeessaatti deebi'e" }],
  [["returned_to_registrar", "responded_back_to_registrar"],
    { en: "Returned to registrar", am: "ወደ ሬጅስትራር ተመልሷል", or: "Gara galmeessaatti deebi'e" }],
  [["respond_back_prosecutor", "returned_to_prosecutor", "responded_back_to_prosecutor",
    "coordinator_responded_back", "respond_back"],
    { en: "Returned to prosecutor", am: "ለዐቃቤ ሕግ ተመልሷል", or: "Abbaa alangaatti deebi'e" }],
  [["sent_to_court", "submitted_to_court", "forwarded_to_court"],
    { en: "Sent to court", am: "ለፍርድ ቤት ተልኳል", or: "Mana murtiitti ergame" }],
  [["assigned_to_prosecutor"],
    { en: "Assigned to prosecutor", am: "ለዐቃቤ ሕግ ተመድቧል", or: "Abbaa alangaaf ramadame" }],
  [["assigned_to_clerk", "forwarded_to_higher_clerk"],
    { en: "Sent to clerk", am: "ወደ ጸሐፊ ተልኳል", or: "Barreessaatti ergame" }],
  [["forwarded_to_director"],
    { en: "Forwarded to director", am: "ወደ ዳይሬክተር ተልኳል", or: "Daarektaraatti dabarfame" }],
  [["forwarded_to_attorney_general", "forwarded_to_head_of_attorney_general"],
    { en: "Forwarded to the attorney general", am: "ወደ ጠቅላይ ዐቃቤ ሕግ ተልኳል", or: "Abbaa alangaa olaanaatti dabarfame" }],
  [["forwarded_to_deputy"],
    { en: "Forwarded to deputy", am: "ወደ ምክትል ተልኳል", or: "Itti aanaatti dabarfame" }],
  [["approved", "granted", "bail_granted", "prohibition_request_approved", "asset_recovery_request_approved"],
    { en: "Approved", am: "ጸድቋል", or: "Raggaasifame" }],
  [["rejected", "reject", "bail_rejected"],
    { en: "Rejected", am: "ውድቅ ተደርጓል", or: "Didame" }],
  [["completed", "recovered"],
    { en: "Completed", am: "ተጠናቋል", or: "Xumurame" }],
  [["final_verdict_given", "prohibition_final_verdict", "asset_recovery_final_verdict"],
    { en: "Final verdict given", am: "የመጨረሻ ፍርድ ተሰጥቷል", or: "Murtiin xumuraa kenname" }],
  [["case_terminated_by_court", "terminated", "case_dismissed", "terminated_by_prosecutor"],
    { en: "Terminated", am: "ተቋርጧል", or: "Addaan citeera" }],
  [["on_appeal"], { en: "On appeal", am: "በይግባኝ ላይ", or: "Ol'iyyannoo irra" }],
  [["on_casation"], { en: "On cassation", am: "በሰበር ላይ", or: "Ijibbaata irra" }],
  [["closed_by_prosecutor", "closed_by_cordinator"],
    { en: "Closed", am: "ተዘግቷል", or: "Cufame" }],
  [["pending"], { en: "Pending", am: "በመጠባበቅ ላይ", or: "Eegaa jira" }],
  [["on_hearing", "on_litigation", "bail_litigation", "prohibition_on_litigation", "asset_recovery_on_litigation"],
    { en: "On hearing", am: "በችሎት ላይ", or: "Dhaddacha irra" }],
  [["case_suspended", "suspended"], { en: "Suspended", am: "ታግዷል", or: "Dhorkame" }],
  [["revised", "on_revision", "prohibition_on_revision", "asset_recovery_on_revision"],
    { en: "Under revision", am: "በክለሳ ላይ", or: "Fooyya'aa jira" }],
];

const STATUS_BY_CODE = new Map<string, Words>();
STATUS_WORDS.forEach(([codes, words]) => codes.forEach((c) => STATUS_BY_CODE.set(c, words)));

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const humanize = (code: string) => cap(code.replace(/_/g, " ").toLowerCase());
const normalize = (code: string) => code.trim().toLowerCase().replace(/[\s-]+/g, "_");

export const parseDetails = (status?: string | null) => {
  if (!status || !status.includes("=")) return null;
  const out: { model?: string; reference?: string; values: string[] } = { values: [] };
  status.split(";").forEach((part) => {
    const [key, ...rest] = part.split("=");
    const value = rest.join("=");
    if (key === "m") out.model = decodeURIComponent(value);
    if (key === "r") out.reference = decodeURIComponent(value);
    if (key === "v") out.values = value.split(",").filter(Boolean).map(decodeURIComponent);
  });
  return out;
};

export const statusText = (code: string, lang: Lang, t?: (key: string, opts: any) => unknown) => {
  const words = STATUS_BY_CODE.get(normalize(code));
  if (words) return words[lang];
  return t ? String(t(code, { defaultValue: humanize(code) })) : humanize(code);
};

/** The record model of a notification ("type" is "<app>.<Model>"). */
export const modelOf = (n: any): string =>
  n?.model_name || String(n?.type || "").split(".").pop() || "";

export const notificationText = (
  n: any,
  lang: Lang,
  t?: (key: string, opts: any) => unknown,
): { title: string; message: string } => {
  const details = parseDetails(n?.status);
  const action = String(n?.action || "");
  const recordModel = details?.model || modelOf(n);
  const record = RECORDS[recordModel]?.[lang];
  const actionTitle = ACTIONS[action]?.[lang];
  const title =
    [record && cap(record), actionTitle].filter(Boolean).join(" · ") ||
    (action ? humanize(action) : "");
  const sentence = SENTENCES[lang][action];
  if (!details || !record || !sentence) return { title, message: n?.message || "" };
  const ref = details.reference ? ` '${details.reference}'` : "";
  const status = details.values.map((v) => statusText(v, lang, t)).join(", ");
  return { title, message: sentence(record, ref, status) };
};

const AGO: Record<Lang, { now: string; m: string; h: string; d: string; fmt: (n: number, u: string) => string }> = {
  en: { now: "just now", m: "min", h: "h", d: "d", fmt: (n, u) => `${n} ${u} ago` },
  am: { now: "አሁን", m: "ደቂቃ", h: "ሰዓት", d: "ቀን", fmt: (n, u) => `ከ${n} ${u} በፊት` },
  or: { now: "amma", m: "daqiiqaa", h: "sa'aatii", d: "guyyaa", fmt: (n, u) => `${u} ${n} dura` },
};

/** "5 min ago" / "ከ5 ደቂቃ በፊት" / "daqiiqaa 5 dura". */
export const timeAgoText = (timestamp: string | undefined, lang: Lang) => {
  if (!timestamp) return "";
  const seconds = Math.max(0, (Date.now() - new Date(timestamp).getTime()) / 1000);
  if (Number.isNaN(seconds)) return "";
  const w = AGO[lang];
  if (seconds < 60) return w.now;
  if (seconds < 3600) return w.fmt(Math.floor(seconds / 60), w.m);
  if (seconds < 86400) return w.fmt(Math.floor(seconds / 3600), w.h);
  return w.fmt(Math.floor(seconds / 86400), w.d);
};
